import gsap from 'gsap';
import appStore from '../utils/state';
import onKlaviyoNewsletterFormSubmit from '../utils/onKlaviyoNewsletterFormSubmit';
import ButtonModular from './button-modular';

export default class NewsletterModal extends HTMLElement {
  private showTl: gsap.core.Timeline | null = null;
  private hideTl: gsap.core.Timeline | null = null;
  private successTl: gsap.core.Timeline | null = null;

  private form: HTMLFormElement | null = null;
  private emailInput: HTMLInputElement | null = null;
  private nameInput: HTMLInputElement | null = null;
  private submitButton: ButtonModular | null = null;
  private main: HTMLDivElement | null = null;
  private success: HTMLDivElement | null = null;
  private closeButton: HTMLButtonElement | null = null;

  private klaviyoPublicApiKey: string | null = null;
  private klaviyoNewsletterListId: string | null = null;
  private klaviyoNewsletterSourceId: string | null = null;

  private isSubmitting = false;
  private isSubmitted = false;

  private unsubscribeFromNewsletterModalOpenState: (() => void) | null = null;

  private onNewsletterModalToggleBound = this.onNewsletterModalToggle.bind(this);
  private onCloseButtonClickBound = this.onCloseButtonClick.bind(this);
  private onFormInputBound = this.onFormInput.bind(this);
  private onFormSubmitBound = this.onFormSubmit.bind(this);

  connectedCallback(): void {
    this.klaviyoPublicApiKey = this.dataset.klaviyoPublicApiKey || null;
    this.klaviyoNewsletterListId = this.dataset.klaviyoNewsletterListId || null;
    this.klaviyoNewsletterSourceId = this.dataset.klaviyoNewsletterSourceId || null;

    this.form = this.querySelector('[data-newsletter-modal-ref="form"]');
    this.emailInput = this.querySelector('[data-newsletter-modal-ref="email"]');
    this.nameInput = this.querySelector('[data-newsletter-modal-ref="name"]');
    this.submitButton = this.querySelector('[data-newsletter-modal-ref="submit-button"]');
    this.main = this.querySelector('[data-newsletter-modal-ref="main"]');
    this.success = this.querySelector('[data-newsletter-modal-ref="success"]');
    this.closeButton = this.querySelector('[data-newsletter-modal-ref="close-button"]');

    this.addEventListeners();
    this.subscribe();
    this.updateButtonState();
  }

  addEventListeners(): void {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseButtonClickBound);
    }

    if (this.form) {
      this.form.addEventListener('input', this.onFormInputBound);
      this.form.addEventListener('submit', this.onFormSubmitBound);
    }
  }

  removeEventListeners(): void {
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.onCloseButtonClickBound);
    }

    if (this.form) {
      this.form.removeEventListener('input', this.onFormInputBound);
      this.form.removeEventListener('submit', this.onFormSubmitBound);
    }
  }

  subscribe(): void {
    this.unsubscribeFromNewsletterModalOpenState = appStore.subscribeKey(
      'isNewsletterModalOpen',
      this.onNewsletterModalToggleBound,
    );
  }

  unsubscribe(): void {
    if (this.unsubscribeFromNewsletterModalOpenState) {
      this.unsubscribeFromNewsletterModalOpenState();
    }
  }

  isValidEmail(email: string | undefined): boolean {
    if (!email) {
      return false;
    }

    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    return emailRegex.test(email.trim());
  }

  isFormValid(): boolean {
    const email = this.emailInput?.value;
    const name = this.nameInput?.value.trim() ?? '';

    return this.isValidEmail(email) && name.length > 0;
  }

  updateButtonState(): void {
    if (this.isSubmitted || !this.submitButton) {
      return;
    }

    if (this.isFormValid()) {
      this.submitButton.gotToStateIndex(1);
    } else {
      this.submitButton.gotToStateIndex(0);
    }
  }

  onFormInput(): void {
    this.updateButtonState();
  }

  async onFormSubmit(event: Event): Promise<void> {
    event.preventDefault();

    if (this.isSubmitting || this.isSubmitted || !this.isFormValid()) {
      return;
    }

    const formData = new FormData(this.form as HTMLFormElement);
    const nickname = formData.get('nickname') as string;

    if (nickname) {
      return;
    }

    const email = this.emailInput?.value.trim() ?? '';
    const name = this.nameInput?.value.trim() ?? '';

    this.isSubmitting = true;

    // try {
    //   const success = await onKlaviyoNewsletterFormSubmit({
    //     email,
    //     firstName: name,
    //     klaviyoPublicApiKey: this.klaviyoPublicApiKey,
    //     klaviyoNewsletterListId: this.klaviyoNewsletterListId,
    //   });

    //   if (!success) {
    //     console.error('Klaviyo newsletter subscription failed');
    //     return;
    //   }

    //   this.onSuccess();
    // } catch (error) {
    //   console.error(error);
    // } finally {
    //   this.isSubmitting = false;
    // }
  }

  onSuccess(): void {
    this.isSubmitted = true;

    if (this.successTl) {
      this.successTl.kill();
    }

    this.successTl = gsap.timeline();

    if (this.main) {
      this.successTl.to(this.main, { autoAlpha: 0, duration: 0.3, ease: 'power3.out' }, 0);
    }

    if (this.success) {
      this.successTl.to(this.success, { autoAlpha: 1, duration: 0.3, ease: 'power3.out' }, 0.4);
    }
  }

  reset(): void {
    this.isSubmitting = false;
    this.isSubmitted = false;

    if (this.successTl) {
      this.successTl.kill();
    }

    this.form?.reset();

    if (this.main) {
      gsap.set(this.main, { autoAlpha: 1 });
    }

    if (this.success) {
      gsap.set(this.success, { autoAlpha: 0 });
    }

    if (this.submitButton) {
      this.submitButton.gotToStateIndex(0);
    }

    this.updateButtonState();
  }

  onCloseButtonClick(): void {
    appStore.actions.closeNewsletterModal();
  }

  onNewsletterModalToggle(isNewsletterModalOpen: boolean): void {
    if (isNewsletterModalOpen) {
      this.show();
    } else {
      this.hide();
    }
  }

  show(): void {
    this.reset();

    if (this.hideTl) {
      this.hideTl.kill();
    }
    if (this.showTl) {
      this.showTl.kill();
    }
    this.showTl = gsap.timeline();

    this.showTl.fromTo(this, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.2, ease: 'power2.out' }, 0);
    this.showTl.fromTo(this, { y: '5%' }, { y: 0, force3D: true, duration: 0.7, ease: 'expo.out' }, 0);
  }

  hide(): void {
    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }
    this.hideTl = gsap.timeline();

    this.hideTl.to(this, { autoAlpha: 0, duration: 0.1, ease: 'power2.out' }, 0);
    this.hideTl.to(this, { y: '5%', duration: 0.3, force3D: true, ease: 'expo.out' }, 0);
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.unsubscribe();

    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }
    if (this.successTl) {
      this.successTl.kill();
    }
  }
}

customElements.define('newsletter-modal', NewsletterModal);
